import type { ReactNode } from "react";

export function InfoCard({ children }: { children: ReactNode }) {
  return (
    <div className="py-3 px-4 text-center text-body-medium-semibold text-extended-pgasilver-brand-on-color bg-extended-pgasilver-brand-color rounded-lg">
      {children}
    </div>
  );
}

interface InlineSearchResultsProps<T> {
  results: T[];
  isLoading: boolean;
  getKey: (item: T) => string;
  renderItem: (item: T) => ReactNode;
  noResultsMessage?: string;
  loadingMessage?: string;
  className?: string;
  highlightedIndex?: number;
  onHighlightChange?: (index: number) => void;
}

export default function InlineSearchResults<T>({
  results,
  isLoading,
  getKey,
  renderItem,
  noResultsMessage = "No results found",
  loadingMessage = "Searching...",
  className,
  highlightedIndex = -1,
  onHighlightChange
}: InlineSearchResultsProps<T>) {
  if (isLoading) {
    return (
      <div className={className}>
        <div className="py-3 text-center text-sys-brand-on-surface-variant">{loadingMessage}</div>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className={className}>
        <InfoCard>{noResultsMessage}</InfoCard>
      </div>
    );
  }

  return (
    <div className={className} role="listbox">
      {results.map((item, index) => (
        <div
          key={getKey(item)}
          role="option"
          aria-selected={index === highlightedIndex}
          className={`rounded-lg p-2 transition-colors hover:bg-sys-brand-surface-container-high ${
            index === highlightedIndex ? "bg-sys-brand-surface-container-high" : ""
          }`}
          onMouseEnter={() => onHighlightChange?.(index)}
        >
          {renderItem(item)}
        </div>
      ))}
    </div>
  );
}
